import { normalizeInquiryEmailSource } from '../../Utils/inquiryEmailSource';
import { buildEmailAllowlist } from '../../Map/utils/shareAllowlist';

export const CAD_NOT_FOUND_MESSAGE = 'Esta lista no existe.';
export const CAD_LOGIN_VIEW_MESSAGE = 'Iniciá sesión para ver esta lista.';

/**
 * UUID canónico del proyecto CAD (`cad_projects.id`).
 * Cards / getmycadparcels pueden traer `project_id`.
 * @param {object|null|undefined} project
 * @returns {string}
 */
export function getCadProjectId(project) {
  return String(project?.id || project?.project_id || '').trim();
}

function cadProjectSlug(project) {
  return String(project?.slug || project?.project_slug || '').trim();
}

/**
 * Misma lista CAD: UUID canónico o slug.
 * @param {object|null|undefined} a
 * @param {object|null|undefined} b
 */
export function cadProjectsMatch(a, b) {
  if (!a || !b) return false;
  const idA = getCadProjectId(a);
  const idB = getCadProjectId(b);
  if (idA && idB && idA === idB) return true;
  const slugA = cadProjectSlug(a);
  const slugB = cadProjectSlug(b);
  return Boolean(slugA && slugB && slugA === slugB);
}

/**
 * GET /getcadproject es la vista (display_mode, georef, imagen). La card
 * del listado solo aporta ACL: si mezclamos `display_mode` / `lat` de la
 * card, un Guardar de Referenciar pinta el plano negro o deja el pin.
 *
 * @param {object|null|undefined} loaded
 * @param {object|null|undefined} listProject
 * @returns {object|null|undefined}
 */
export function hydrateCadProjectFromListCard(loaded, listProject) {
  if (!loaded) return loaded;
  const canEdit = loaded.can_edit === true || listProject?.can_edit === true;
  if (!listProject) return { ...loaded, can_edit: canEdit };
  return {
    ...loaded,
    id: getCadProjectId(loaded) || getCadProjectId(listProject),
    project_id: getCadProjectId(loaded) || getCadProjectId(listProject),
    can_manage: loaded.can_manage ?? listProject.can_manage,
    can_publish: loaded.can_publish ?? listProject.can_publish,
    can_edit: canEdit,
    is_shared: typeof loaded.is_shared === 'boolean'
      ? loaded.is_shared
      : listProject.is_shared,
    access_mode: loaded.access_mode ?? listProject.access_mode,
  };
}

/**
 * El listado (`getcadprojects` / shared-with-me) y la card abierta no son
 * fuente de la imagen base: eso sale de `GET /getcadproject`. Si el catálogo
 * trae URL (stale) o `null`, no pisa el GET. Tras un DELETE, el GET viene
 * `canvas_image: null` y la card todavía tiene la URL: mezclarla la “revive”.
 *
 * @param {object} summary
 * @returns {object}
 */
function catalogSummaryWithoutCanvasImage(summary) {
  if (!Object.prototype.hasOwnProperty.call(summary, 'canvas_image')
    && !Object.prototype.hasOwnProperty.call(summary, 'show_plan_image')) {
    return summary;
  }
  const next = { ...summary };
  delete next.canvas_image;
  delete next.show_plan_image;
  return next;
}

/**
 * Si el proyecto ya tiene vista (GET / reload), la card no pisa
 * `display_mode` / `lat` / georef: deja el plano negro o el pin fantasma.
 * Un stub del click sí toma esos campos del catálogo.
 *
 * @param {object} project
 * @param {object} summary
 * @returns {object}
 */
function catalogSummaryForMerge(project, summary) {
  const withoutImage = catalogSummaryWithoutCanvasImage(summary);
  const hasLiveView = project?.display_mode != null || project?.srid != null
    || project?.geometry_source != null;
  if (!hasLiveView) return withoutImage;
  const next = { ...withoutImage };
  delete next.display_mode;
  delete next.lat;
  delete next.lng;
  delete next.srid;
  delete next.geometry_source;
  delete next.georef;
  delete next.ref_lot_id;
  delete next.parcels;
  delete next.bounds;
  return next;
}

/**
 * El click del mapa abre un stub (overlay: `can_manage`, sin `can_publish`).
 * Completa ACL con la fila de `getcadprojects` / shared-with-me.
 *
 * @param {object|null|undefined} project
 * @param {object[]} [owned]
 * @param {object[]} [shared]
 */
export function resolveCadProjectFromCatalog(project, owned = [], shared = []) {
  if (!project) return project;
  const match = [...(Array.isArray(owned) ? owned : []), ...(Array.isArray(shared) ? shared : [])]
    .find((row) => cadProjectsMatch(project, row));
  return match ? mergeCadProjectSummary(project, catalogSummaryForMerge(project, match)) : project;
}

/**
 * Lookup para GET/PATCH/DELETE: el UUID es obligatorio.
 * El slug queda solo como pretty opcional (`/#/lista/:slug/:id`); el lookup es por id.
 * Un string suelto se interpreta como `id` (nunca como slug).
 * @param {string|object|null|undefined} projectOrLookup
 * @returns {{ id: string, slug: string }}
 */
export function cadProjectQuery(projectOrLookup) {
  if (projectOrLookup == null) return { id: '', slug: '' };
  if (typeof projectOrLookup === 'string') {
    return { id: projectOrLookup.trim(), slug: '' };
  }
  return {
    id: getCadProjectId(projectOrLookup),
    slug: String(projectOrLookup.slug || projectOrLookup.project_slug || '').trim(),
  };
}

export function canManageCadProject(project) {
  return Boolean(project?.can_manage);
}

export function canPublishCadProject(project) {
  return Boolean(project?.can_publish);
}

/** Editor de **esa** colección (`can_edit`). No es `can_manage` de empresa. */
export function canEditCadCollection(project) {
  return project?.can_edit === true;
}

/** Mismo eje ver que listas: `is_shared`. */
export function isCadProjectShared(project) {
  if (typeof project?.is_shared === 'boolean') return project.is_shared;
  return project?.is_published === true;
}

/**
 * Badge de card: none / public / restricted. Igual que listas.
 * @param {object|null} project
 * @returns {'none'|'public'|'restricted'}
 */
export function getCadShareBadgeType(project) {
  if (!isCadProjectShared(project)) return 'none';
  return project?.access_mode === 'restricted' ? 'restricted' : 'public';
}

export function showsCadCadastralBase(project) {
  return project?.show_cadastral_base === true;
}

export function mergeCadProjectSummary(project, summary) {
  if (!project) return project;
  if (!summary || typeof summary !== 'object') return project;
  const isShared = typeof summary.is_shared === 'boolean'
    ? summary.is_shared
    : (typeof summary.is_published === 'boolean' ? summary.is_published : project.is_shared);
  return {
    ...project,
    id: getCadProjectId(summary) || project.id,
    project_id: getCadProjectId(summary) || project.project_id,
    is_shared: isShared,
    access_mode: summary.access_mode ?? project.access_mode,
    allowlist: summary.allowlist ?? project.allowlist,
    show_cadastral_base: summary.show_cadastral_base ?? project.show_cadastral_base,
    show_plan_image: Object.prototype.hasOwnProperty.call(summary, 'show_plan_image')
      ? summary.show_plan_image
      : project.show_plan_image,
    display_mode: summary.display_mode ?? project.display_mode,
    canvas_image: Object.prototype.hasOwnProperty.call(summary, 'canvas_image')
      ? summary.canvas_image
      : project.canvas_image,
    srid: summary.srid ?? project.srid,
    geometry_source: summary.geometry_source ?? project.geometry_source,
    bounds: summary.bounds ?? project.bounds,
    can_manage: summary.can_manage ?? project.can_manage,
    can_publish: summary.can_publish ?? project.can_publish,
    can_edit: summary.can_edit ?? project.can_edit,
    name: summary.name ?? project.name,
    share_template: summary.share_template ?? project.share_template,
    brand_id: Object.prototype.hasOwnProperty.call(summary, 'brand_id')
      ? summary.brand_id
      : project.brand_id,
    inquiry_email_source: Object.prototype.hasOwnProperty.call(summary, 'inquiry_email_source')
      ? summary.inquiry_email_source
      : project.inquiry_email_source,
    owner_branding: summary.owner_branding ?? project.owner_branding,
    ref_lot_id: Object.prototype.hasOwnProperty.call(summary, 'ref_lot_id')
      ? summary.ref_lot_id
      : project.ref_lot_id,
    georef: Object.prototype.hasOwnProperty.call(summary, 'georef')
      ? summary.georef
      : project.georef,
    georef_src_corners: Object.prototype.hasOwnProperty.call(summary, 'georef_src_corners')
      ? summary.georef_src_corners
      : project.georef_src_corners,
  };
}

/**
 * GET completo tras georef / clear: `ref_lot_id` viene siempre (UUID o
 * `null`). `georef` se omite si está vacío. Un merge con `??` dejaría el
 * ancla vieja.
 *
 * @param {object|null} project
 * @param {object|null} loaded
 */
export function mergeCadProjectFromReload(project, loaded) {
  if (!project) return loaded || project;
  if (!loaded || typeof loaded !== 'object') return project;
  const next = mergeCadProjectSummary(project, loaded);
  const fullGet = loaded.display_mode != null && loaded.srid != null;
  if (!fullGet) return next;
  return {
    ...next,
    parcels: loaded.parcels ?? next.parcels,
    lat: loaded.display_mode === 'map'
      ? (Object.prototype.hasOwnProperty.call(loaded, 'lat') ? loaded.lat : next.lat)
      : null,
    lng: loaded.display_mode === 'map'
      ? (Object.prototype.hasOwnProperty.call(loaded, 'lng') ? loaded.lng : next.lng)
      : null,
    // GET completo: si omiten la clave (post-DELETE) no reusar la URL vieja.
    canvas_image: Object.prototype.hasOwnProperty.call(loaded, 'canvas_image')
      ? loaded.canvas_image
      : null,
    ref_lot_id: Object.prototype.hasOwnProperty.call(loaded, 'ref_lot_id')
      ? (loaded.ref_lot_id || null)
      : next.ref_lot_id,
    georef: loaded.georef || null,
    georef_src_corners: Object.prototype.hasOwnProperty.call(loaded, 'georef_src_corners')
      ? loaded.georef_src_corners
      : next.georef_src_corners,
  };
}

export function emailsFromCadAllowlist(allowlist) {
  return Array.from(new Set(
    (Array.isArray(allowlist) ? allowlist : [])
      .filter((entry) => entry?.type === 'email' && entry?.value)
      .map((entry) => String(entry.value).trim().toLowerCase())
      .filter(Boolean),
  ));
}

/**
 * @param {string[]} emails
 * @param {{ asEditorEmails?: string[] }} [options]
 */
export function cadAllowlistFromEmails(emails, options) {
  return buildEmailAllowlist(emails, options);
}

/**
 * Marca + invite SES (mismas claves que listas). Invite en restricted
 * (ver/editar) o público (solo `as_editor`); vacío + invite → 400.
 * `invite_message` es opcional; si viene vacío no se manda.
 *
 * CAD: body con `id` (no `list_id`) y sin `scope`. Sacar editor =
 * `DELETE /deletecadprojectmember`, no unshare.
 *
 * `brand_id` se copia por presencia de la clave, no por tener valor: el PATCH
 * es parcial y omitirlo **conserva** lo que ya tenía el proyecto. Para volver
 * a la principal hay que mandar `brand_id: null` explícito.
 * `inquiry_email_source` quedó de compat: el diálogo ya no lo manda.
 *
 * @param {object} body
 * @returns {object}
 */
export function pickCadShareExtras(body) {
  const extras = {};
  if (body?.share_template === 'personalized' || body?.share_template === 'default') {
    extras.share_template = body.share_template;
  }
  if (body && Object.prototype.hasOwnProperty.call(body, 'brand_id')) {
    extras.brand_id = body.brand_id ? String(body.brand_id) : null;
  }
  if (body && Object.prototype.hasOwnProperty.call(body, 'inquiry_email_source')) {
    extras.inquiry_email_source = body.inquiry_email_source == null
      ? null
      : normalizeInquiryEmailSource(body.inquiry_email_source);
  }
  const canInvite = body?.access_mode === 'restricted' || body?.access_mode === 'public';
  if (canInvite && body?.send_invite === true) {
    const recipients = Array.isArray(body.invite_emails)
      ? body.invite_emails.map((email) => String(email).trim().toLowerCase()).filter(Boolean)
      : [];
    if (recipients.length > 0) {
      extras.send_invite = true;
      extras.invite_emails = recipients;
      const message = typeof body.invite_message === 'string' ? body.invite_message.trim() : '';
      if (message) extras.invite_message = message.slice(0, 2000);
    }
  }
  return extras;
}

/**
 * `data.invite` del PATCH. El share se guarda igual si SES falla.
 *
 * @param {object|null|undefined} payload
 * @returns {object|null}
 */
export function readCadInvite(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const invite = payload.invite
    ?? payload.project?.invite
    ?? payload.data?.invite;
  return invite && typeof invite === 'object' ? invite : null;
}

export function isCadUnauthorized(status) {
  return status === 401;
}

export function isCadNotFound(status) {
  return status === 404;
}

export function isCadForbidden(status) {
  return status === 403;
}
